import { useCallback, useMemo, useRef, useEffect } from 'react'
import { Row, Col, TreeSelect, Table } from 'antd'
import { SaveFilled } from '@ant-design/icons'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { VariantModelValue, listingsAllValue } from '../../../Constants/Configuration'
import { setPriceListingFilter, resetPriceChanged } from '../../../Slices/Kiosk/PriceSlice'
import { renderTranslation } from '../../../Helpers/GenericHelpers'
import { PriceType, priceStandardHeight } from '../../../Constants/TableConfiguration'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  getListingBySize,
  getPriceDataByType,
  retrieveListingList,
  basePricelist,
  PricetoSave,
} from '../../../Helpers/PricesHelper'
import { savePricesChanges } from 'Services/Kiosk/PriceService'
import { handlePricesData, handleBasePricesData } from 'Services/Kiosk/ArticleServices'
import PricesEditHeader from './PriceEditHeader'
import PricesListing from 'Components/Table/Priceslisting'
function PricesEdit(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const basePriceData: basePricelist[] = useAppSelector(
    state => state.present.articleDetail.articleSelected.basePriceData,
  )
  const priceListingFilter = useAppSelector(state => state.present.price.priceListingFilter)
  const priceTypeFilter = useAppSelector(state => state.present.price.priceTypeFilter)
  const priceTypeShow = useAppSelector(state => state.present.price.priceTypeShow)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const workspacePricesVisible = useAppSelector(state => state.present.configuration.workspacePricesVisible)
  const filterDateRange = useAppSelector(state => state.present.price.priceFilterDateRange)
  const changePrices: PricetoSave[] = useAppSelector(state => state.present.price.priceChanged)
  const rowKey = useAppSelector(state => state.present.article.rowKeysSelected)

  const isArticleSelected = articleSelected.fetchedPriceData
  const priceTableRef = useRef<any>()

  const listingAll = useMemo(() => {
    return {
      value: listingsAllValue,
      label: renderTranslation(language, TranslationKeys.priceSectionDropdownListingAllValue),
    }
  }, [language])

  const [listingsList, isBySize] = useMemo(() => {
    return [retrieveListingList(basePriceData), getListingBySize(basePriceData)]
  }, [basePriceData])

  const listingsSelectList = useMemo(() => {
    const listings =
      listingsList
        ?.filter(x => x !== VariantModelValue)
        .map(x => {
          return { value: x, label: x }
        }) ?? []

    listings.unshift(listingAll)

    return listings
  }, [listingsList, listingAll])

  const priceTableColumns = useMemo(() => {
    return [
      {
        title: (
          <>
            <PricesEditHeader
              priceTypeFilter={priceTypeFilter}
              priceListingFilterPurchase={PriceType.purchase}
              priceListingFilterSell={PriceType.sell}
              sellAndPurchaseValue={{
                sellPrice: basePriceData?.find(x => x.name === VariantModelValue)?.prices[0].sellPrice ?? 0,
                buyPrice: basePriceData?.find(x => x.name === VariantModelValue)?.prices[0].buyPrice ?? 0,
              }}
              isBySize={isBySize}
              priceTypeShow={priceTypeShow}
              size={null}
            />
          </>
        ),
        dataIndex: isBySize ? 'startDate' : 'fakeIndex',
        key: 'startDateColumn',
        width: 100,
        className: 'listing-start-date',
        render: (dateTime, record) => {
          return (
            <Row>
              <Col span={24}>{record.size.sizeName}</Col>
            </Row>
          )
        },
      },
    ]
  }, [isBySize, basePriceData, priceTypeFilter, priceTypeShow, filterDateRange])

  const onListingSelect = useCallback(
    listingValues => {
      if (
        (listingValues.find(x => x.value === listingsAllValue) &&
          !priceListingFilter.find(x => x.value === listingsAllValue)) ||
        listingValues.length === 0
      ) {
        dispatch(setPriceListingFilter([listingAll]))
      } else {
        const listingToSave = listingValues
          .filter(x => x.value !== listingsAllValue)
          .map(x => {
            return {
              value: x.value,
              label: x.value,
            }
          })

        dispatch(setPriceListingFilter(listingToSave))
      }
    },
    [priceListingFilter, dispatch, listingAll],
  )

  useEffect(() => {
    dispatch(setPriceListingFilter([listingAll]))
  }, [isArticleSelected])

  const saveChanges = e => {
    if (changePrices.length === 0) return
    savePricesChanges(rowKey[0], changePrices).finally(() => {
      dispatch(resetPriceChanged())
      handleBasePricesData(rowKey[0], dispatch)()
      handlePricesData(rowKey[0], dispatch)()
    })
  }
  const priceDataFiltered = useMemo(() => {
    return getPriceDataByType(basePriceData, priceTypeFilter)
  }, [basePriceData, priceTypeFilter, filterDateRange])

  const listingsSelected = useMemo(() => {
    const listingValues = priceListingFilter.map(x => x.value)
    return retrieveListingList(priceDataFiltered).filter(x =>
      listingValues.indexOf(listingsAllValue) !== -1
        ? listingValues.indexOf(listingsAllValue) !== -1
        : listingValues.indexOf(x) !== -1,
    )
  }, [priceDataFiltered, priceListingFilter])

  const pricerRecordTable = useMemo(() => {
    return basePriceData && basePriceData.length > 0
      ? Object.values(basePriceData.find(x => x.isBaseForSize)?.prices ?? [])
      : []
  }, [basePriceData])

  return workspacePricesVisible ? (
    <>
      <Row className='overflow-hidden custom-scroll flex-nowrap' justify={'start'}>
        <Col span={4} className=' pt-4 prices-history-box ' id='discount-type-tabs'>
          <SaveFilled
            className={'generic-icon save-icon' + (changePrices.length > 0 ? ' active' : '')}
            onClick={saveChanges}
          />
        </Col>
        <Col>
          <div className='upper-label pl-2 pt-2'>
            {renderTranslation(language, TranslationKeys.priceSectionDropdownListingLabel)}
          </div>
          <TreeSelect
            treeData={listingsSelectList}
            treeCheckable={true}
            treeCheckStrictly={true}
            onChange={onListingSelect}
            value={
              priceListingFilter.length === 0
                ? [
                    {
                      value: listingsAllValue,
                      label: renderTranslation(language, TranslationKeys.priceSectionDropdownListingAllValue),
                    },
                  ]
                : priceListingFilter
            }
            disabled={true}
          />
        </Col>
      </Row>
      <Row className='overflow-hidden custom-scroll flex-nowrap' ref={priceTableRef}>
        <Col span={4} className='pl-2 pr-2 mb-2 pt-2 '>
          {isArticleSelected ? (
            <PricesEditHeader
              priceTypeFilter={priceTypeFilter}
              priceListingFilterPurchase={PriceType.purchase}
              priceListingFilterSell={PriceType.sell}
              sellAndPurchaseValue={{
                sellPrice: basePriceData?.find(x => x.name === VariantModelValue)?.prices[0].sellPrice ?? 0,
                buyPrice: basePriceData?.find(x => x.name === VariantModelValue)?.prices[0].buyPrice ?? 0,
              }}
              priceTypeShow={priceTypeShow}
              size={null}
            />
          ) : null}
        </Col>
        <Col span={20} id='price-table-box' className='pl-2 pr-2 mb-2 scroll-overflow-price-table'>
          <div className='multiple-table-container d-flex pt-2'>
            {/* first child void */}
            <div></div>
            {listingsSelected && listingsSelected.length > 0 && isArticleSelected
              ? listingsSelected
                  .filter(x => x !== VariantModelValue)
                  .map(listing => {
                    return (
                      <PricesListing
                        key={'listing-table-' + listing}
                        activeListing={listing}
                        distinctPriceDates={
                          basePriceData.find(x => x.name.toLocaleLowerCase() === listing.toLocaleLowerCase())?.prices ??
                          0
                        }
                        priceTypeShow={priceTypeShow}
                      />
                    )
                  })
              : null}
          </div>
        </Col>
      </Row>
    </>
  ) : null
}

export default PricesEdit
